/* 分类页专用控制器：沿用 Shopify 分面、section_id 与购物车 sections 协议。 */
(() => {
  if (customElements.get('pg-collection-experience')) return;

  class PGCollectionExperience extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true });
      } else this.initialize();
    }

    initialize() {
      if (!this.isConnected) return;
      this.listeners = new AbortController();
      this.mobile = window.matchMedia('(max-width: 959px)');
      this.requestVersion = 0;
      this.filterOpen = false;
      this.pageNumber = Math.max(1, Number(new URL(location.href).searchParams.get('cv_page')) || 1);
      this.selectedSort = this.sortFromURL(new URL(location.href));
      this.sortChosen = this.sortExplicit(new URL(location.href));
      this.results = this.querySelector('[data-cv2-results]');
      this.items = [...this.results.querySelectorAll('[data-cv2-grid] > [data-cv2-item]')];
      this.originalItems = [...this.items];
      this.allLoaded = !this.results.dataset.nextUrl;
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.cartTrigger = null;
      this.ownedScrollLock = false;
      this.compareItems = new Map();
      this.trayDismissed = false;
      this.addEventListener('click', (event) => this.onClick(event), { signal: this.listeners.signal });
      this.addEventListener('change', (event) => this.onChange(event), { signal: this.listeners.signal });
      this.addEventListener('input', (event) => this.onInput(event), { signal: this.listeners.signal });
      this.addEventListener('submit', (event) => this.onSubmit(event), { signal: this.listeners.signal });
      document.addEventListener('keydown', (event) => this.onKeyDown(event), { signal: this.listeners.signal });
      document.addEventListener('click', (event) => {
        const sort = this.querySelector('[data-cv2-sort]');
        if (!sort.contains(event.target)) sort.open = false;
      }, { signal: this.listeners.signal });
      window.addEventListener('popstate', () => {
        if (location.pathname === this.dataset.collectionUrl) {
          this.sortChosen = this.sortExplicit(new URL(location.href));
          this.loadResults(new URL(location.href), { updateHistory: false, page: Number(new URL(location.href).searchParams.get('cv_page')) || 1 });
        }
      }, { signal: this.listeners.signal });
      this.onBreakpoint = () => {
        // 切换断点时按第一项商品保留位置，取消手机弹层的滚动锁。
        const firstIndex = (this.pageNumber - 1) * this.pageSize(!this.mobile.matches);
        this.pageNumber = Math.floor(firstIndex / this.pageSize()) + 1;
        this.setFiltersOpen(this.filterOpen, false);
        this.renderProducts();
        // 导航显隐在共用外壳脚本 assets/pg-cv2-chrome.js，换断点后通知它重算。
        document.dispatchEvent(new CustomEvent('pg-cv2:nav-update'));
      };
      this.mobile.addEventListener('change', this.onBreakpoint);
      this.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('close', () => this.onDialogClose(dialog), { signal: this.listeners.signal });
        dialog.addEventListener('cancel', () => {}, { signal: this.listeners.signal });
        dialog.addEventListener('click', (event) => {
          const box = dialog.getBoundingClientRect();
          if (event.target === dialog && (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)) dialog.close();
        }, { signal: this.listeners.signal });
      });
      if (this.cart) {
        this.cartObserver = new MutationObserver(() => this.onCartState());
        this.cartObserver.observe(this.cart, { attributes: true, attributeFilter: ['class'], subtree: true });
      }
      // UGC 模块（<pg-cv2-ugc>，assets/pg-cv2-ugc.js）请求打开灯箱：由本页统一管滚动锁与焦点，与对比弹框一致。
      this.addEventListener('pg-cv2:open-dialog', (event) => {
        event.preventDefault();
        this.openDialog(event.detail.dialog, event.detail.trigger);
      }, { signal: this.listeners.signal });
      this.initializeTrayGestures();
      this.syncPriceRange();
      this.markSort();
      this.renderProducts();
      if (!this.allLoaded) this.loadResults(new URL(location.href), { updateHistory: false, page: this.pageNumber });
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.requestController?.abort();
      this.mobile?.removeEventListener('change', this.onBreakpoint);
      this.cartObserver?.disconnect();
      clearTimeout(this.filterTimer);
      if (this.ownedScrollLock) document.body.style.overflow = this.previousOverflow;
      document.body.classList.remove('pg-cv2-has-tray');
      this.started = false;
    }

    pageSize(mobile = this.mobile.matches) {
      return Math.max(1, Number(mobile ? this.dataset.mobilePerPage : this.dataset.perPage) || (mobile ? 6 : 10));
    }

    sortExplicit(url) {
      return url.searchParams.has('sort_by') || url.searchParams.has('cv_sort');
    }

    sortFromURL(url) {
      return url.searchParams.get('cv_sort') || url.searchParams.get('sort_by') || 'manual';
    }

    capacity(item) {
      const text = item.querySelector('[data-cv2-product]').dataset.capacity;
      const match = String(text || '').replace(/,/g, '').match(/\d+(?:\.\d+)?/);
      if (!match) return null;
      const amount = Number(match[0]);
      return /kg/i.test(text) ? amount * 2.2046226218 : amount;
    }

    orderedItems() {
      if (!this.selectedSort.startsWith('capacity-')) return [...this.originalItems];
      const descending = this.selectedSort === 'capacity-desc';
      return this.originalItems.map((item, index) => ({ item, index, capacity: this.capacity(item) })).sort((a, b) => {
        if (a.capacity === null && b.capacity === null) return a.index - b.index;
        if (a.capacity === null) return 1;
        if (b.capacity === null) return -1;
        return (descending ? b.capacity - a.capacity : a.capacity - b.capacity) || a.index - b.index;
      }).map((entry) => entry.item);
    }

    renderProducts() {
      const grid = this.results.querySelector('[data-cv2-grid]');
      const ordered = this.orderedItems();
      const pageSize = this.pageSize();
      const pages = Math.max(1, Math.ceil(ordered.length / pageSize));
      this.pageNumber = Math.min(pages, Math.max(1, this.pageNumber));
      const start = (this.pageNumber - 1) * pageSize;
      const visible = new Set(ordered.slice(start, start + pageSize));
      grid.querySelector('.pg-cv2-promo')?.remove();
      // 同一批真实 DOM 节点重排，末行不会增宽，加购按钮状态也能保留。
      ordered.forEach((item) => { item.hidden = !visible.has(item); grid.append(item); });
      const promoTemplate = this.querySelector('[data-cv2-promo]');
      if (promoTemplate && this.dataset.showPromo !== 'false' && visible.size >= (this.mobile.matches ? 4 : 7)) {
        const insertAfter = ordered[start + (this.mobile.matches ? 3 : 6)];
        const promo = promoTemplate.content.firstElementChild.cloneNode(true);
        // 本页恰好 7 个时推广卡在最后，筛选展开 2 列会在其上方留空格，由 CSS 把第 7 个商品排到推广卡之后
        if (visible.size === 7) promo.dataset.cv2PromoLast = '';
        insertAfter.after(promo);
      }
      this.results.querySelector('[data-cv2-empty]').hidden = ordered.length > 0;
      const pager = this.results.querySelector('[data-cv2-pagination]');
      pager.hidden = ordered.length === 0;
      pager.querySelector('[data-cv2-page-label]').textContent = `${this.pageNumber} / ${pages}`;
      pager.querySelector('[data-cv2-page-step="-1"]').disabled = this.pageNumber <= 1;
      pager.querySelector('[data-cv2-page-step="1"]').disabled = this.pageNumber >= pages;
      this.dataset.loadedCount = String(ordered.length);
      this.syncCompareButtons();
    }

    markSort() {
      const chosen = this.sortChosen;
      this.querySelectorAll('[data-cv2-sort-option]').forEach((button) => button.setAttribute('aria-pressed', String(chosen && button.dataset.cv2SortOption === this.selectedSort)));
    }

    async fetchDocument(url, signal) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) controller.abort();
      const timer = setTimeout(abort, 15000);
      try {
        const response = await fetch(url, { signal: controller.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (!document.querySelector('[data-cv2-results]')) throw new Error('返回内容不包含目标分类模块');
        return document;
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
      }
    }

    async loadResults(url, { updateHistory = true, page = 1, scroll = false } = {}) {
      this.requestController?.abort();
      const controller = new AbortController();
      this.requestController = controller;
      const version = ++this.requestVersion;
      this.results.setAttribute('aria-busy', 'true');
      this.showStatus('');
      const browserURL = new URL(url);
      browserURL.searchParams.delete('section_id');
      browserURL.searchParams.delete('page');
      if (page > 1) browserURL.searchParams.set('cv_page', String(page));
      else browserURL.searchParams.delete('cv_page');
      const requestURL = new URL(browserURL);
      requestURL.searchParams.set('section_id', this.dataset.sectionId);
      requestURL.searchParams.delete('cv_page');
      const sort = this.sortFromURL(browserURL);
      if (sort.startsWith('capacity-')) requestURL.searchParams.set('sort_by', 'manual');
      try {
        let incoming = await this.fetchDocument(requestURL, controller.signal);
        const first = incoming;
        const nodes = [];
        const seen = new Set();
        const visitedPages = new Set([requestURL.href]);
        for (;;) {
          const results = incoming.querySelector('[data-cv2-results]');
          results.querySelectorAll('[data-cv2-grid] > [data-cv2-item]').forEach((item) => {
            const id = item.querySelector('[data-cv2-product]').dataset.productId;
            if (!seen.has(id)) { seen.add(id); nodes.push(item); }
          });
          const next = results.dataset.nextUrl;
          if (!next) break;
          const nextURL = new URL(next, requestURL);
          nextURL.searchParams.set('section_id', this.dataset.sectionId);
          if (visitedPages.has(nextURL.href)) throw new Error('分页链接重复');
          visitedPages.add(nextURL.href);
          incoming = await this.fetchDocument(nextURL, controller.signal);
        }
        if (version !== this.requestVersion || controller.signal.aborted) return;
        const expectedCount = Number(first.querySelector('[data-cv2-results]').dataset.count);
        if (nodes.length !== expectedCount) throw new Error(`商品收集不完整：${nodes.length}/${expectedCount}`);
        const grid = this.results.querySelector('[data-cv2-grid]');
        grid.replaceChildren(...nodes);
        const openGroups = this.openFilterNames();
        this.querySelector('[data-cv2-filter-content]').innerHTML = first.querySelector('[data-cv2-filter-content]').innerHTML;
        this.restoreFilterGroups(openGroups);
        this.syncPriceRange();
        this.items = [...nodes];
        this.originalItems = [...nodes];
        this.allLoaded = true;
        this.selectedSort = sort;
        this.pageNumber = page;
        this.results.dataset.count = String(expectedCount);
        this.renderProducts();
        this.markSort();
        if (updateHistory) history.pushState({ cv2: true }, '', browserURL);
        if (scroll) this.scrollCatalog();
      } catch (error) {
        if (version !== this.requestVersion || controller.signal.aborted) return;
        // 保留上一批商品与 URL；当前点击的排序选项也回到已生效状态。
        this.showStatus(this.dataset.requestError);
        this.markSort();
      } finally {
        if (version === this.requestVersion) this.results.removeAttribute('aria-busy');
      }
    }

    filterURL() {
      const url = new URL(location.href);
      [...url.searchParams.keys()].forEach((key) => { if (key.startsWith('filter.') || key === 'page' || key === 'cv_page') url.searchParams.delete(key); });
      const form = this.querySelector('[data-cv2-filter-form]');
      for (const [name, value] of new FormData(form)) {
        if (name.startsWith('filter.') && String(value).trim() !== '') url.searchParams.append(name, value);
      }
      return url;
    }

    syncPriceRange() {
      const ranges = this.querySelector('.pg-cv2-price-ranges');
      if (!ranges) return;
      const min = ranges.querySelector('[data-cv2-range="min"]');
      const max = ranges.querySelector('[data-cv2-range="max"]');
      const limit = Number(max.max) || 1;
      ranges.style.setProperty('--cv2-range-min', `${Number(min.value) / limit * 100}%`);
      ranges.style.setProperty('--cv2-range-max', `${Number(max.value) / limit * 100}%`);
    }

    validatePrices() {
      const min = this.querySelector('[data-cv2-price="min"]');
      const max = this.querySelector('[data-cv2-price="max"]');
      if (!min || !max) return true;
      max.setCustomValidity('');
      if (min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)) {
        max.setCustomValidity(`${this.querySelector('[data-cv2-price="min"]').getAttribute('aria-label')} ≤ ${max.getAttribute('aria-label')}`);
      }
      return this.querySelector('[data-cv2-filter-form]').reportValidity();
    }

    applyFilters() {
      if (!this.validatePrices()) return;
      const url = this.filterURL();
      if (this.mobile.matches) this.setFiltersOpen(false);
      // 吸顶筛选时商品区顶部已滚出视口：结果变少后回到商品区顶部，避免停在页脚、筛选面板跟着离开（运营 10-09）
      const scrolledPast = this.querySelector('#cv2-catalog').getBoundingClientRect().top < 0;
      this.loadResults(url, { scroll: scrolledPast });
    }

    // 筛选分组默认全部收起（运营 10-09）；重载筛选内容时保留用户已展开的分组。
    openFilterNames() {
      return [...this.querySelectorAll('.pg-cv2-filter[open]')].map((group) => group.dataset.filterName);
    }

    restoreFilterGroups(names = []) {
      this.querySelectorAll('.pg-cv2-filter').forEach((group) => { group.open = names.includes(group.dataset.filterName); });
    }

    setFiltersOpen(open, focus = true) {
      this.filterOpen = Boolean(open);
      const aside = this.querySelector('[data-cv2-filters]');
      const toggle = this.querySelector('[data-cv2-filter-toggle]');
      const label = toggle.querySelector('[data-cv2-filter-label]');
      aside.hidden = !open;
      aside.setAttribute('role', this.mobile.matches ? 'dialog' : 'region');
      aside.setAttribute('aria-label', label.dataset.closedLabel);
      if (open && this.mobile.matches) aside.setAttribute('aria-modal', 'true');
      else aside.removeAttribute('aria-modal');
      this.querySelector('[data-cv2-filter-backdrop]').hidden = !(open && this.mobile.matches);
      this.querySelector('[data-cv2-layout]').classList.toggle('is-filter-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      label.textContent = open ? label.dataset.openLabel : label.dataset.closedLabel;
      this.updateScrollLock();
      if (focus && this.mobile.matches) {
        if (open) aside.querySelector('button,input,summary')?.focus({ preventScroll: true });
        else toggle.focus({ preventScroll: true });
      }
    }

    onInput(event) {
      if (event.target.matches('[data-cv2-range]')) {
        const input = this.querySelector(`[data-cv2-price="${event.target.dataset.cv2Range}"]`);
        input.value = event.target.value;
        const min = this.querySelector('[data-cv2-price="min"]');
        const max = this.querySelector('[data-cv2-price="max"]');
        const minRange = this.querySelector('[data-cv2-range="min"]');
        const maxRange = this.querySelector('[data-cv2-range="max"]');
        // 空价格输入代表未限定边界，不能按数字 0 把另一个滑块拉到当前值。
        if (Number(minRange.value) > Number(maxRange.value)) {
          const other = event.target.dataset.cv2Range === 'min' ? max : min;
          other.value = event.target.value;
          this.querySelector(`[data-cv2-range="${other.dataset.cv2Price}"]`).value = event.target.value;
        }
        this.syncPriceRange();
      }
    }

    onChange(event) {
      if (event.target.matches('[data-cv2-price]')) {
        const range = this.querySelector(`[data-cv2-range="${event.target.dataset.cv2Price}"]`);
        if (range) range.value = event.target.value === '' ? (event.target.dataset.cv2Price === 'min' ? range.min : range.max) : event.target.value;
        this.syncPriceRange();
      }
      if (event.target.closest('[data-cv2-filter-form]') && !this.mobile.matches) {
        clearTimeout(this.filterTimer);
        this.filterTimer = setTimeout(() => this.applyFilters(), 250);
      }
    }

    onClick(event) {
      if (event.target.matches('[data-cv2-filter-backdrop]')) { this.setFiltersOpen(false); return; }
      const target = event.target.closest('button,a');
      if (!target) return;
      if (target.matches('[data-cv2-filter-toggle]')) this.setFiltersOpen(!this.filterOpen);
      else if (target.matches('[data-cv2-filter-close]') || event.target.matches('[data-cv2-filter-backdrop]')) this.setFiltersOpen(false);
      else if (target.matches('[data-cv2-sort-option]')) {
        this.sortChosen = true;
        const url = new URL(location.href);
        url.searchParams.delete('cv_page');
        if (target.dataset.cv2SortOption.startsWith('capacity-')) {
          url.searchParams.set('cv_sort', target.dataset.cv2SortOption);
          url.searchParams.delete('sort_by');
        } else { url.searchParams.set('sort_by', target.dataset.cv2SortOption); url.searchParams.delete('cv_sort'); }
        this.querySelector('[data-cv2-sort]').open = false;
        this.loadResults(url);
      } else if (target.matches('[data-cv2-clear]')) {
        this.querySelector('[data-cv2-filter-form]').reset();
        const url = new URL(location.href);
        [...url.searchParams.keys()].forEach((key) => { if (key.startsWith('filter.') || key === 'cv_page' || key === 'page') url.searchParams.delete(key); });
        this.loadResults(url);
      } else if (target.matches('[data-cv2-page-step]')) {
        this.pageNumber += Number(target.dataset.cv2PageStep);
        this.renderProducts();
        const url = new URL(location.href);
        if (this.pageNumber > 1) url.searchParams.set('cv_page', String(this.pageNumber));
        else url.searchParams.delete('cv_page');
        history.pushState({ cv2: true }, '', url);
        this.scrollCatalog();
      } else if (target.matches('[data-cv2-compare]')) this.toggleCompare(target.dataset.cv2Compare);
      // 运营 10-09 第 4 轮：PC 托盘右上角 × 只收起托盘、保留已选（与手机下滑收起一致）。
      else if (target.matches('[data-cv2-tray-close]')) this.dismissTray();
      else if (target.matches('[data-cv2-tray-remove]')) this.toggleCompare(target.dataset.cv2TrayRemove);
      else if (target.matches('[data-cv2-tray-compare]')) this.openComparison(target);
      else if (target.matches('[data-cv2-dialog-close]')) target.closest('dialog').close();
      else if (target.tagName === 'A' && target.getAttribute('href')?.startsWith('#')) {
        const destination = this.querySelector(target.getAttribute('href'));
        if (destination) { event.preventDefault(); destination.scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'start' }); }
      }
    }

    onSubmit(event) {
      if (event.target.matches('[data-cv2-filter-form]')) { event.preventDefault(); this.applyFilters(); }
      else if (event.target.matches('[data-cv2-add]')) { event.preventDefault(); this.addToCart(event.target); }
    }

    async addToCart(form) {
      const button = form.querySelector('[type="submit"]');
      if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
      const error = form.querySelector('[role="alert"]');
      error.hidden = true;
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading', 'is-active');
      button.querySelector('.loading__spinner')?.classList.remove('hidden');
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const body = new FormData(form);
        if (this.cart) {
          body.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
          body.append('sections_url', location.pathname);
          this.cart.setActiveElement(button);
        }
        const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', { method: 'POST', body, signal: controller.signal, headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' } });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || this.dataset.requestError);
        const dialog = form.closest('dialog');
        if (dialog?.open) dialog.close();
        this.cartTrigger?.classList.remove('is-active');
        this.cartTrigger = button;
        this.cartWasOpen = false;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: body.get('id'), cartData: data });
        }
        if (this.cart) {
          this.cart.renderContents(data);
          this.cart.classList.remove('is-empty');
          if (typeof setFooterCart === 'function') setFooterCart();
        } else location.assign(window.routes?.cart_url || '/cart');
      } catch (failure) {
        error.textContent = failure.name === 'AbortError' ? this.dataset.requestError : failure.message;
        error.hidden = false;
        button.classList.remove('is-active');
      } finally {
        clearTimeout(timeout);
        button.classList.remove('loading');
        button.removeAttribute('aria-disabled');
        button.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    }

    onCartState() {
      const open = this.cart.classList.contains('active') || Boolean(this.cart.querySelector('.cart-notification.active'));
      if (open) this.cartWasOpen = true;
      else if (this.cartWasOpen) { this.cartTrigger?.classList.remove('is-active'); this.cartTrigger = null; this.cartWasOpen = false; }
    }

    openDialog(dialog, trigger) {
      dialog.cv2Trigger = trigger;
      trigger.classList.add('is-active');
      dialog.showModal();
      this.updateScrollLock();
    }

    onDialogClose(dialog) {
      dialog.cv2Trigger?.classList.remove('is-active');
      dialog.cv2Trigger?.focus({ preventScroll: true });
      dialog.cv2Trigger = null;
      this.updateScrollLock();
    }

    toggleCompare(id) {
      // 托盘已被收起且仍有已选：点已选的 Compare 只重新显示托盘，不移除。
      if (this.trayDismissed && this.compareItems.has(id)) { this.renderTray(); return; }
      if (this.compareItems.has(id)) this.compareItems.delete(id);
      else {
        const tray = this.querySelector('[data-cv2-tray]');
        if (this.compareItems.size >= 3) {
          // 已满且托盘被收起：先重新显示托盘，上限提示才看得见。
          if (this.trayDismissed) this.renderTray();
          const notice = tray.querySelector('[data-cv2-tray-notice]');
          notice.textContent = tray.dataset.limitText;
          notice.hidden = false;
          clearTimeout(this.noticeTimer);
          this.noticeTimer = setTimeout(() => { notice.hidden = true; }, 3000);
          return;
        }
        const item = this.originalItems.find((entry) => entry.querySelector('[data-cv2-product]').dataset.productId === id);
        if (!item) return;
        const card = item.querySelector('[data-cv2-product]');
        // 存快照：筛选/翻页换掉商品节点后，已选商品仍能对比。
        this.compareItems.set(id, {
          name: card.dataset.model,
          spec: [card.dataset.tv, card.dataset.capacity].filter(Boolean).join(' / '),
          thumb: card.dataset.thumb,
          html: item.querySelector('[data-cv2-comparison]').innerHTML,
        });
      }
      this.renderTray();
      this.syncCompareButtons();
    }

    // 运营：手机托盘下滑或点托盘外任意位置即收起；已选商品保留，再点 Compare 重新弹出。
    initializeTrayGestures() {
      const tray = this.querySelector('[data-cv2-tray]');
      let startY = null, offset = 0;
      tray.addEventListener('touchstart', (event) => {
        if (!this.mobile.matches || event.touches.length !== 1) return;
        startY = event.touches[0].clientY; offset = 0;
        tray.classList.add('is-dragging');
      }, { passive: true, signal: this.listeners.signal });
      tray.addEventListener('touchmove', (event) => {
        if (startY === null) return;
        offset = Math.max(0, event.touches[0].clientY - startY);
        tray.style.transform = offset ? `translateY(${offset}px)` : '';
      }, { passive: true, signal: this.listeners.signal });
      const end = () => {
        if (startY === null) return;
        startY = null;
        tray.classList.remove('is-dragging');
        tray.style.transform = '';
        if (offset > 50) this.dismissTray();
      };
      tray.addEventListener('touchend', end, { signal: this.listeners.signal });
      tray.addEventListener('touchcancel', end, { signal: this.listeners.signal });
      document.addEventListener('click', (event) => {
        // ×被 renderTray 重绘后已脱离文档，tray.contains(target) 会误判；composedPath 在派发开始时固定。
        if (!this.mobile.matches || tray.hidden || event.composedPath().includes(tray)) return;
        if (event.target.closest('[data-cv2-compare], dialog')) return;
        this.dismissTray();
      }, { signal: this.listeners.signal });
    }

    dismissTray() {
      const tray = this.querySelector('[data-cv2-tray]');
      tray.hidden = true;
      this.trayDismissed = true;
      tray.querySelector('[data-cv2-tray-notice]').hidden = true;
      document.body.classList.remove('pg-cv2-has-tray');
    }

    renderTray() {
      const tray = this.querySelector('[data-cv2-tray]');
      const list = tray.querySelector('[data-cv2-tray-items]');
      const template = tray.querySelector('[data-cv2-tray-item]');
      list.replaceChildren(...[...this.compareItems].map(([id, data]) => {
        const node = template.content.firstElementChild.cloneNode(true);
        // 同一项有两个移除入口（手机图标 ×、PC 文字链），由 CSS 按端显示其一。
        node.querySelectorAll('[data-cv2-tray-remove]').forEach((remove) => {
          remove.dataset.cv2TrayRemove = id;
          remove.setAttribute('aria-label', `${tray.dataset.removeLabel} ${data.name}`);
        });
        const thumb = node.querySelector('.pg-cv2-tray__thumb');
        if (data.thumb) thumb.src = data.thumb; else thumb.hidden = true;
        node.querySelector('.pg-cv2-tray__name').textContent = data.name;
        node.querySelector('.pg-cv2-tray__spec').textContent = data.spec;
        return node;
      }));
      tray.querySelector('[data-cv2-tray-count]').textContent = String(this.compareItems.size);
      tray.querySelector('[data-cv2-tray-compare]').disabled = this.compareItems.size < 2;
      tray.hidden = this.compareItems.size === 0;
      document.body.classList.toggle('pg-cv2-has-tray', !tray.hidden);
      if (!tray.hidden) this.trayDismissed = false;
      if (tray.hidden) tray.querySelector('[data-cv2-tray-notice]').hidden = true;
    }

    syncCompareButtons() {
      this.querySelectorAll('[data-cv2-compare]').forEach((button) => button.setAttribute('aria-pressed', String(this.compareItems.has(button.dataset.cv2Compare))));
    }

    openComparison(trigger) {
      if (this.compareItems.size < 2) return;
      const dialog = this.querySelector('[data-cv2-compare-dialog]');
      const grid = dialog.querySelector('[data-cv2-comparison-grid]');
      const dots = dialog.querySelector('[data-cv2-comparison-dots]');
      grid.innerHTML = [...this.compareItems.values()].map((data) => data.html).join('');
      dots.replaceChildren(...[...this.compareItems.keys()].map(() => document.createElement('span')));
      if (!grid.cv2DotsBound) {
        grid.cv2DotsBound = true;
        grid.addEventListener('scroll', () => this.syncComparisonDots(), { passive: true, signal: this.listeners.signal });
      }
      this.openDialog(dialog, trigger);
      // 弹框关闭时 scrollLeft 赋值无效，必须在 showModal 之后复位。
      grid.scrollLeft = 0;
      this.syncComparisonDots();
    }

    syncComparisonDots() {
      const dialog = this.querySelector('[data-cv2-compare-dialog]');
      const grid = dialog.querySelector('[data-cv2-comparison-grid]');
      const dots = [...dialog.querySelector('[data-cv2-comparison-dots]').children];
      const max = grid.scrollWidth - grid.clientWidth;
      const cards = [...grid.children];
      // 取 offsetLeft 与 scrollLeft 最接近的卡片；滑到尽头取最后一张（吸附位置不等于进度比例）。
      const index = max <= 1 || !cards.length ? 0
        : grid.scrollLeft >= max - 1 ? cards.length - 1
        : cards.reduce((best, card, i) => Math.abs(card.offsetLeft - cards[0].offsetLeft - grid.scrollLeft) < Math.abs(cards[best].offsetLeft - cards[0].offsetLeft - grid.scrollLeft) ? i : best, 0);
      dots.forEach((dot, i) => dot.classList.toggle('is-current', i === index));
    }

    updateScrollLock() {
      const locked = Boolean(this.querySelector('dialog[open]')) || (this.filterOpen && this.mobile.matches);
      // 遮罩打开时隐藏第三方悬浮按钮（✦ AI 助手 / Shulex），避免挡住抽屉与弹框（运营 10-12）
      document.body.classList.toggle('pg-cv2-overlay-open', locked);
      if (locked && !this.ownedScrollLock) { this.previousOverflow = document.body.style.overflow; this.ownedScrollLock = true; document.body.style.overflow = 'hidden'; }
      else if (!locked && this.ownedScrollLock) { document.body.style.overflow = this.previousOverflow; this.ownedScrollLock = false; }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        this.querySelector('[data-cv2-sort]').open = false;
        if (this.filterOpen && this.mobile.matches) this.setFiltersOpen(false);
      }
      if (event.key !== 'Tab' || !this.filterOpen || !this.mobile.matches) return;
      const nodes = [...this.querySelector('[data-cv2-filters]').querySelectorAll('button,input,summary,a[href]')].filter((el) => !el.disabled && el.getClientRects().length);
      const first = nodes[0], last = nodes.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }

    showStatus(text) { const status = this.querySelector('[data-cv2-status]'); status.textContent = text; status.hidden = !text; }
    reducedMotion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    scrollCatalog() { this.querySelector('#cv2-catalog').scrollIntoView({ behavior: this.reducedMotion() ? 'auto' : 'smooth', block: 'start' }); }
  }

  customElements.define('pg-collection-experience', PGCollectionExperience);
})();
